import { updateBackUrl } from "@utils/getLocalStorage";
import { timezone } from "@utils/StringConstants";
import he from "he";
import momentTz from "moment-timezone";
import moment from "moment/moment";

export const decodeHTML = (val: any) => {
  if (!val || typeof val !== "string") return val;

  let previousVal;
  let decodedVal = val;
  do {
    previousVal = decodedVal;
    decodedVal = he.decode(decodedVal);
  } while (decodedVal !== previousVal);
  return decodedVal
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '');
};


export const encodeHTML = (val: any) => {
  let ret = val;
  if (val && typeof val == "string") {
    ret = he.encode(ret);
  }
  return ret;
};

export function isOdd(num: number): boolean {
  return num % 2 !== 0;
}

export function getTimeDifference(time1: number, time2: number, unit: "seconds" | "minutes"): number {
  const diff = Math.abs(time1 - time2);
  const factor = unit === "seconds" ? 1000 : 60000;

  return Math.floor(diff / factor);
}

export function numberToWord(number: number): string {
  if (number < 0 || number > 100) {
    return `${number}`;
  }

  const ones = [
    "zero",
    "first",
    "second",
    "third",
    "fourth",
    "fifth",
    "sixth",
    "seventh",
    "eighth",
    "ninth",
    "tenth",
    "eleventh",
    "twelfth",
    "thirteenth",
    "fourteenth",
    "fifteenth",
    "sixteenth",
    "seventeenth",
    "eighteenth",
    "nineteenth",
  ];
  const tens = ["", "", "twenty", "thirty", "forty", "fifty", "sixty", "seventy", "eighty", "ninety"];

  number++;

  if (number < 20) {
    return ones[number].charAt(0).toUpperCase() + ones[number].slice(1);
  }

  const digit = number % 10;
  const ten = Math.floor(number / 10);

  return `${tens[ten]}${digit === 0 ? "" : "-" + ones[digit].charAt(0).toUpperCase() + ones[digit].slice(1)}`;
}

export const sortString = (a: any, b: any, fieldName: string) => {
  a[fieldName] = a[fieldName] ? a[fieldName] : "";
  b[fieldName] = b[fieldName] ? b[fieldName] : "";
  const fieldA = a[fieldName].toUpperCase(); // ignore upper and lowercase
  const fieldB = b[fieldName].toUpperCase(); // ignore upper and lowercase
  // const fieldA = a[fieldName].trim(); // case sensitive
  // const fieldB = b[fieldName].trim(); // case sensitive
  if (fieldA < fieldB) {
    return -1;
  }
  if (fieldA > fieldB) {
    return 1;
  }
  return 0;
};

export const isReviewerApproved = (email: any, reviewers: any[]) => {
  if (reviewers && reviewers.length > 0) {
    for (const reviewer of reviewers) {
      if (reviewer.emailAddress === email) {
        return reviewer.status === true;
      }
    }
  }
  return false;
};

export const getFormattedDate = (date: any) => {
  if (date) {
    return momentTz(date).format("DD MMM YYYY hh:mm:ss A"); //.tz(timezone)
  }
  return date;
};

export const getStartDate = (date: any) => {
  if (date) {
    return momentTz(date).format("DD MMM YYYY"); // only date
  }
  return date;
};

export const getDateFormat = (date:any) => {
  if (date) {
    const possibleFormats = ["YYYY-MM-DD", "DD/MM/YYYY", "YYYY MM DD"];
    const parsedDate = moment(date, possibleFormats, true);

    if (parsedDate.isValid()) {
      return parsedDate.format("DD MMM YYYY");
    }
  }
  return date; 
};

export const formatDateTime = (val: any) => {
  const date = moment(decodeHTML(val), ["DD/MM/YYYY HH:mm", "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss"], true);
  return date.isValid() ? date.format("DD/MM/YYYY HH:mm:ss") : "";
};

export const getFormattedDateTime = (value: string) => {
  console.log("getFormattedDateTime", value);
  if (!value) {
    return ""; // Return empty string if value is null or undefined
  }

  const date = moment(value, moment.ISO_8601, true); // Parse as ISO 8601 format

  if (!date.isValid()) {
    return "Invalid date"; // Return a fallback string if the date is invalid
  }

  return date.format("DD MMM YYYY HH:mm:ss"); // Format as 18 Sep 2024 13:57:58
};

export const switchHasRole = (records: any, key: string) => {
  if (records?.roles?.includes(key)) {
    return true;
  }
  return false;
};

function convertMonthsToMonthsAndDays(monthsDecimal: any) {
  const months = Math.floor(monthsDecimal);
  const remainingDaysDecimal = monthsDecimal - months;
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  const year = new Date().getFullYear();
  if (year % 4 === 0 && (year % 100 !== 0 || year % 400 === 0)) {
    daysInMonth[1] = 29;
  }
  const remainingDays = Math.round(remainingDaysDecimal * daysInMonth[months]);
  return `${months} ${months > 1 ? "Months" : "Month"} and ${remainingDays} ${remainingDays > 1 ? "Days" : "Day"}`;
}

export const monthChange = (commencementDate: any, completionDate: any) => {
  if (commencementDate && completionDate) {
    const duration = moment.duration(moment(completionDate, "YYYY-MM-DD").diff(moment(commencementDate, "YYYY-MM-DD")));
    const years = duration.years();
    const months = duration.months();
    const days = duration.days();
    let result: string = "";
    if (years !== 0) {
      result += `${years} ${years !== 1 ? "years" : "year"}, `;
    }
    if (months !== 0) {
      result += `${months} ${months !== 1 ? "months" : "month"}, `;
    }
    if (days !== 0) {
      result += `${days} ${days !== 1 ? "days" : "day"}, `;
    }
    result = result.replace(/, $/, "");
    return result;
  }
};

export const scrollTop = () => {
  setTimeout(() => {
    let element: any = document.getElementById("contentWrapperContent");
    if (element) {
      const parentElement: any = element.parentNode;
      parentElement.scrollTo({
        top: 0,
        behavior: "smooth",
      });
      // parentElement.scrollIntoView({
      //   behavior: "smooth",
      //   block: "start",
      //   inline: "nearest",
      // });
    }
  }, 0);
};

export const scrollToElement = (id: string) => {
  setTimeout(() => {
    let element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
        inline: "nearest",
      });
    }
  }, 0);
};

export const getTotalCost = (cost: any) => {
  let temp: any = cost.replace(/,/gi, "");
  return Number(temp);
};

export const getUrlwithParams = () => {
  const currentURL = window.location.href;
  const { pathname, search } = new URL(currentURL);
  updateBackUrl(`${pathname}${search}`);
  return `${pathname}${search}`;
};

export const hasKeyString = (key: string) => {
  if (window.location.pathname.split("/")[1].split("-").includes(key)) {
    return true;
  } else {
    return false;
  }
};
